import { Box, Drawer, IconButton, ModalClose, useTheme } from "@mui/joy";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { PanelLeftClose, PanelLeft } from "lucide-react";
import NavigationPanel from "../NavigationPanel";
import BottomNav from "../BottomNav";

interface AppNavigationProps {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  isMenuOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
}

const SIDEBAR_EXPANDED = "15.5rem";
const SIDEBAR_COLLAPSED = "4.5rem";

export default function AppNavigation({
  collapsed,
  setCollapsed,
  isMenuOpen,
  setIsMenuOpen,
}: AppNavigationProps) {
  const theme = useTheme();
  const location = useLocation();

  const [isTablet, setIsTablet] = useState(
    window.innerWidth >= 600 && window.innerWidth < 1024,
  );
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsTablet(width >= 600 && width < 1024);
      setIsMobile(width < 600);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isTablet && !collapsed) {
      setCollapsed(true);
    }
    if (isMobile) {
      setIsMenuOpen(false);
    }
  }, [
    location.pathname,
    isTablet,
    isMobile,
    collapsed,
    setCollapsed,
    setIsMenuOpen,
  ]);

  const sidebarWidth =
    isTablet || collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED;
  const isOverlay = isTablet && !collapsed;

  return (
    <>
      {!isMobile && (
        <Box
          component="aside"
          sx={{
            flexShrink: 0,
            width: sidebarWidth,
            height: "100%",
            position: "relative",
            transition: "width 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            zIndex: isOverlay ? 1000 : 10,
            bgcolor: "background.surface",
          }}
        >
          <Box
            sx={{
              width: isOverlay ? SIDEBAR_EXPANDED : sidebarWidth,
              height: "100%",
              position: isOverlay ? "absolute" : "relative",
              top: 0,
              left: 0,
              zIndex: 1,
              transition: "width 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow: isOverlay ? theme.shadow.md : "none",
              bgcolor: "background.surface",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <NavigationPanel collapsed={isTablet ? true : collapsed} />

            {!isTablet && (
              <Box
                sx={{
                  position: "absolute",
                  top: 14,
                  right: -12,
                  zIndex: 2,
                }}
              >
                <IconButton
                  size="sm"
                  variant="soft"
                  color="neutral"
                  onClick={() => setCollapsed(!collapsed)}
                  sx={{
                    "--IconButton-size": "24px",
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.surface",
                    boxShadow: "sm",
                  }}
                >
                  {collapsed ? (
                    <PanelLeft size={14} />
                  ) : (
                    <PanelLeftClose size={14} />
                  )}
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>
      )}

      <Drawer
        open={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        sx={{
          display: { xs: "block", sm: "none" },
          [`& .MuiDrawer-content`]: {
            width: SIDEBAR_EXPANDED,
            p: 0,
          },
        }}
      >
        <ModalClose sx={{ top: 12, right: 12 }} />
        <NavigationPanel collapsed={false} />
      </Drawer>

      {isMobile && <BottomNav onOpenMenu={() => setIsMenuOpen(true)} />}
    </>
  );
}
